// src/components/ShoutOutForm.js
import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress, Typography, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';

const PatientDailyLogType = { JOURNAL_ENTRY: "journal_entry" };

const ShoutOutForm = ({ open, onClose, episodeId }) => { // Added episodeId prop
    const { authFetch, activeEpisode } = useAuth();
    const [feedback, setFeedback] = useState('');
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (logPayload) => authFetch('/api/v1/recovery-logs/daily', { method: 'POST', body: logPayload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['patientDashboardData'] });
            onClose();
            setFeedback('');
        },
        onError: (error) => {
            console.error("Error saving shout-out:", error);
            alert(`Failed to submit feedback. Please try again.`);
        },
    });

    const handleSave = () => {
        const shoutOutText = `Staff Shout-out/Feedback: ${feedback}`;
        // Prioritize prop, fall back to context, or null
        const targetEpisodeId = episodeId || activeEpisode?.id || null;
        
        mutation.mutate({
            episode_of_care_id: targetEpisodeId,
            log_type: PatientDailyLogType.JOURNAL_ENTRY,
            data: { journal_text: shoutOutText },
            log_date: new Date().toISOString().split('T')[0],
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Appreciation / Feedback
                <IconButton aria-label="close" onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Is there a staff member who went above and beyond? We'd love to hear about it!
                </Typography>
                <TextField
                    label="Your message..."
                    multiline
                    rows={6}
                    fullWidth
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    size="small"
                    autoFocus
                />
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    startIcon={mutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                    variant="contained" 
                    disabled={mutation.isPending || !feedback.trim()}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShoutOutForm;